// ✅ src/pages/VehicleV2/JobCardEntry.jsx  (UPDATED DESIGN TEMPLATE)
// Backend expected:
//   app.use("/api/jobCardEntry", jobCardEntry);
//
// APIs used:
//   GET  https://nissi-india.com/api/jobCardEntry/init?loginUserId=&branchId=&branchType=&vehicleId=
//   GET  https://nissi-india.com/api/jobCardEntry/list?loginUserId=&branchId=&branchType=&vehicleId=&page=1&limit=10
//   POST https://nissi-india.com/api/jobCardEntry/save
//
// Design template aligned with ProductionBOMListing / JobCardListing:
// - Search/Entry criteria panel (blue-50 / gray-800/50)
// - px-4 py-2 inputs + blue focus ring
// - striped table + hover rows
// - skin-colored Submit + bordered Reset
// Functional rules kept:
// - vehicleId from query/localStorage
// - submit sends dd/MM/yyyy entryDate

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaSave, FaUndo } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/jobCardEntry";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");

const ymdToDDMMYYYY = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-");
  if (!y || !m || !d) return "";
  return `${pad2(d)}/${pad2(m)}/${y}`;
};

const parseDDMMYYYY = (s) => {
  const parts = String(s || "").split("/");
  if (parts.length !== 3) return null;

  const [dd, mm, yyyy] = parts;
  const d = Number(dd);
  const m = Number(mm);
  const y = Number(yyyy);

  if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) {
    return null;
  }

  const dt = new Date(y, m - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== m - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }

  return dt;
};

const parseAnyDateToYMD = (raw) => {
  const val = clean(raw);
  if (!val) return "";

  const ddmmyyyy = parseDDMMYYYY(val);
  if (ddmmyyyy) {
    return `${ddmmyyyy.getFullYear()}-${pad2(ddmmyyyy.getMonth() + 1)}-${pad2(
      ddmmyyyy.getDate()
    )}`;
  }

  const dt = new Date(val);
  if (!Number.isNaN(dt.getTime())) {
    return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(
      dt.getDate()
    )}`;
  }

  return "";
};

const JobCardEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid") || "8";
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const branchId = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim() || "HO",
    [branchTypeLS]
  );
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);
  const loginUserId =
    (typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId")
      : "") ||
    userId ||
    "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const vehicleId = useMemo(() => {
    const sp = new URLSearchParams(location?.search || "");
    const fromQuery = toInt(sp.get("vehicleId") || sp.get("vehicleid"));
    if (fromQuery) return fromQuery;
    return toInt(getLS("vehicleId") || 0);
  }, [location]);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [headerInfo, setHeaderInfo] = useState({
    jobCardNo: "",
    vehicleName: "",
    vehicleRegNo: "",
    vehicleCode: "",
    location: "",
  });

  const [form, setForm] = useState({
    entryDateYMD: "",
    hrKm: "",
    status: "",
    natureOfComplaint: "",
    complaintAttendedBy: "",
  });

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => {
    const t = Math.ceil(total / limit);
    return t > 0 ? t : 1;
  }, [total, limit]);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const readonlyBox = `w-full px-4 py-2 rounded-lg border font-medium ${
    isDark
      ? "bg-gray-700/40 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const mapHeader = useCallback((data) => {
    const h = data?.header || data?.vehicle || data || {};

    return {
      jobCardNo: toText(h?.jobCardNo ?? h?.fld_jobcardno),
      vehicleName: toText(h?.vehicleName ?? h?.fld_vehicle_name),
      vehicleRegNo: toText(h?.vehicleRegNo ?? h?.fld_vehicle_regno),
      vehicleCode: toText(h?.vehicleCode ?? h?.fld_vehicle_code),
      location: toText(h?.location ?? h?.fld_Worklocationname),
    };
  }, []);

  const mapEdit = useCallback((edit) => {
    const raw = edit || {};

    return {
      entryDateYMD: parseAnyDateToYMD(
        raw?.entryDate ??
          raw?.entryDateYMD ??
          raw?.fld_entrydate1 ??
          raw?.fld_entrydate
      ),
      hrKm: toText(raw?.hrKm ?? raw?.fld_hrmtrreading),
      status: toText(raw?.status ?? raw?.fld_status),
      natureOfComplaint: toText(
        raw?.natureOfComplaint ?? raw?.fld_natureofcomplaint
      ),
      complaintAttendedBy: toText(
        raw?.complaintAttendedBy ?? raw?.fld_complaintattendedby
      ),
    };
  }, []);

  const mapRow = useCallback((r) => {
    return {
      id: toInt(r?.id ?? r?.jobCardId ?? r?.fld_jobcardid ?? 0),
      jobCardNo: toText(r?.jobCardNo ?? r?.fld_jobcardno),
      entryDate: toText(r?.entryDate ?? r?.fld_entrydate1 ?? r?.fld_entrydate),
      vehicleCode: toText(r?.vehicleCode ?? r?.fld_vehicle_code),
      vehicleName: toText(r?.vehicleName ?? r?.fld_vehicle_name),
      vehicleRegNo: toText(r?.vehicleRegNo ?? r?.fld_vehicle_regno),
      location: toText(r?.location ?? r?.fld_Worklocationname),
      hrKm: toText(r?.hrKm ?? r?.fld_hrmtrreading),
      status: toText(r?.status ?? r?.fld_status),
      natureOfComplaint: toText(
        r?.natureOfComplaint ?? r?.fld_natureofcomplaint
      ),
      complaintAttendedBy: toText(
        r?.complaintAttendedBy ?? r?.fld_complaintattendedby
      ),
      _raw: r,
    };
  }, []);

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          branchType: clean(branchType),
          vehicleId: toInt(vehicleId, 0),
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      setHeaderInfo(mapHeader(data));

      if (data?.edit) {
        setForm((prev) => ({
          ...prev,
          ...mapEdit(data.edit),
        }));
      }

      if (Array.isArray(data?.grid?.list)) {
        const list = data.grid.list.map(mapRow);
        setRows(list);
        setTotal(toInt(data?.grid?.total, 0));
        setPage(toInt(data?.grid?.page, 1));
        setLimit(toInt(data?.grid?.limit, limit));
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Init failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    loginUserId,
    branchId,
    branchType,
    vehicleId,
    commonHeaders,
    mapHeader,
    mapEdit,
    mapRow,
    limit,
  ]);

  const loadList = useCallback(
    async (nextPage = page, nextLimit = limit) => {
      try {
        setLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            loginUserId: toInt(loginUserId, 0),
            branchId: toInt(branchId, 0),
            branchType: clean(branchType),
            vehicleId: toInt(vehicleId, 0),
            page: toInt(nextPage, 1),
            limit: toInt(nextLimit, 10),
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load list");
        }

        const list = Array.isArray(data?.list) ? data.list.map(mapRow) : [];
        setRows(list);
        setTotal(toInt(data?.total, 0));
        setPage(toInt(data?.page, nextPage));
        setLimit(toInt(data?.limit, nextLimit));
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err?.message || "List failed"
        );
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [
      page,
      limit,
      loginUserId,
      branchId,
      branchType,
      vehicleId,
      commonHeaders,
      mapRow,
    ]
  );

  const validate = () => {
    if (!form.entryDateYMD) {
      toast.warn("Please select Entry Date");
      return false;
    }

    const entryDDMM = ymdToDDMMYYYY(form.entryDateYMD);
    const entryDt = parseDDMMYYYY(entryDDMM);

    if (!entryDt) {
      toast.warn("Invalid Entry Date");
      return false;
    }

    const today = new Date();
    const todayDateOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const entryDateOnly = new Date(
      entryDt.getFullYear(),
      entryDt.getMonth(),
      entryDt.getDate()
    );

    if (entryDateOnly.getTime() > todayDateOnly.getTime()) {
      toast.warn("Please select Date less than or equal to current date.");
      return false;
    }

    if (!clean(form.hrKm)) {
      toast.warn("Hour/Kilo Meter Reading is required");
      return false;
    }

    if (!clean(form.status)) {
      toast.warn("Status is required");
      return false;
    }

    if (!clean(form.natureOfComplaint)) {
      toast.warn("Nature of Complaint is required");
      return false;
    }

    if (!clean(form.complaintAttendedBy)) {
      toast.warn("Complaint Attended by is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      const body = {
        loginUserId: toInt(loginUserId, 0),
        branchId: toInt(branchId, 0),
        branchType: clean(branchType),
        vehicleId: toInt(vehicleId, 0),
        entryDate: ymdToDDMMYYYY(form.entryDateYMD),
        hrKm: clean(form.hrKm),
        status: clean(form.status),
        natureOfComplaint: clean(form.natureOfComplaint),
        complaintAttendedBy: clean(form.complaintAttendedBy),
      };

      const { data } = await axios.post(`${API_BASE}/save`, body, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Save failed");
      }

      toast.success(data?.message || "Saved successfully");
      handleReset();
      await loadList(1, limit);
      setPage(1);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm({
      entryDateYMD: "",
      hrKm: "",
      status: "",
      natureOfComplaint: "",
      complaintAttendedBy: "",
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const canPrev = page > 1;
  const canNext = page < totalPages;
  const isBusy = loading || pageLoading || saving;

  const paginationText = useMemo(() => {
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(total, page * limit);
    return `${from}-${to} of ${total}`;
  }, [page, limit, total]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    loadList(page, limit);
  }, [page, limit, loadList]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">JOB CARD ENTRY</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleBack}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>

              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Job Card Entry
              </h2>
            </div>
          </div>

          {/* ==================== ENTRY FORM ==================== */}
          <div
            className={`p-6 rounded-lg mb-6 ${
              isDark ? "bg-gray-800/50" : "bg-blue-50"
            }`}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-blue-900"
                }`}
              >
                Entry Details
              </h3>

              <div className="flex gap-2">
                <button
                  onClick={handleReset}
                  type="button"
                  disabled={isBusy}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                    isDark
                      ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-white"
                  } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaUndo />
                  Reset
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={isBusy}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isBusy ? "opacity-60 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSave />
                  {saving ? "Saving..." : "Submit"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>Job Card No</label>
                <div className={readonlyBox}>{headerInfo.jobCardNo || "-"}</div>
              </div>

              <div>
                <label className={labelClass}>
                  Entry Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={form.entryDateYMD}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      entryDateYMD: e.target.value,
                    }))
                  }
                  className={inputClass}
                  disabled={isBusy}
                />
              </div>

              <div>
                <label className={labelClass}>
                  Hour/Kilo Meter Reading <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.hrKm}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, hrKm: e.target.value }))
                  }
                  className={inputClass}
                  disabled={isBusy}
                  placeholder="Enter HR / KM"
                />
              </div>

              <div>
                <label className={labelClass}>Vehicle Name</label>
                <div className={readonlyBox}>
                  {headerInfo.vehicleName || "-"}
                </div>
              </div>

              <div>
                <label className={labelClass}>Vehicle Reg No</label>
                <div className={readonlyBox}>
                  {headerInfo.vehicleRegNo || "-"}
                </div>
              </div>

              <div>
                <label className={labelClass}>Vehicle Code</label>
                <div className={readonlyBox}>
                  {headerInfo.vehicleCode || "-"}
                </div>
              </div>

              <div>
                <label className={labelClass}>Location</label>
                <div className={readonlyBox}>{headerInfo.location || "-"}</div>
              </div>

              <div>
                <label className={labelClass}>
                  Status <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.status}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, status: e.target.value }))
                  }
                  className={inputClass}
                  disabled={isBusy}
                  placeholder="Enter status"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Complaint Attended by <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.complaintAttendedBy}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      complaintAttendedBy: e.target.value,
                    }))
                  }
                  className={inputClass}
                  disabled={isBusy}
                  placeholder="Enter name"
                />
              </div>

              <div className="md:col-span-2 lg:col-span-3">
                <label className={labelClass}>
                  Nature of Complaint <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.natureOfComplaint}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      natureOfComplaint: e.target.value,
                    }))
                  }
                  className={inputClass}
                  disabled={isBusy}
                  placeholder="Enter nature of complaint"
                />
              </div>
            </div>
          </div>

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || (loading && rows.length === 0) ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No Records found
                </div>
              </div>
            ) : (
              <>
                <table
                  className={`w-full min-w-[1400px] ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        S.No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Job Card No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Entry Date
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Code
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Name
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Reg No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Location
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Hr/Km
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Nature Of Complaint
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Complaint Attended By
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`${
                      isDark ? "divide-gray-700" : "divide-gray-200"
                    } divide-y`}
                  >
                    {rows.map((r, idx) => (
                      <tr
                        key={r.id || idx}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${
                          isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                        }`}
                      >
                        <td
                          className={`px-6 py-4 text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {(page - 1) * limit + idx + 1}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm font-semibold ${
                            isDark ? "text-green-400" : "text-green-600"
                          }`}
                        >
                          {r.jobCardNo || "-"}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.entryDate || "-"}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm font-medium ${
                            isDark ? "text-blue-400" : "text-blue-600"
                          }`}
                        >
                          {r.vehicleCode || "-"}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm font-medium ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {r.vehicleName || "-"}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.vehicleRegNo || "-"}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.location || "-"}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm font-semibold ${
                            isDark ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          {r.hrKm || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {r.status ? (
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                isDark
                                  ? "bg-yellow-900/40 text-yellow-200"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {r.status}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.natureOfComplaint || "-"}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.complaintAttendedBy || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div
                    className={`text-sm ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {paginationText}
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      disabled={!canPrev || isBusy}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className={`px-4 py-2 rounded-lg border text-sm font-semibold ${
                        canPrev && !isBusy
                          ? isDark
                            ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                            : "border-gray-300 text-gray-700 hover:bg-white"
                          : "opacity-50 cursor-not-allowed border-gray-300"
                      }`}
                    >
                      Prev
                    </button>

                    <div
                      className={`text-sm ${
                        isDark ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Page {page} / {totalPages}
                    </div>

                    <button
                      disabled={!canNext || isBusy}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className={`px-4 py-2 rounded-lg border text-sm font-semibold ${
                        canNext && !isBusy
                          ? isDark
                            ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                            : "border-gray-300 text-gray-700 hover:bg-white"
                          : "opacity-50 cursor-not-allowed border-gray-300"
                      }`}
                    >
                      Next
                    </button>

                    <select
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                      disabled={isBusy}
                      className={inputClass}
                      style={{ width: 120 }}
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n} / page
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default JobCardEntry;